import React, { ReactElement, useCallback, useState } from "react";

import styled, { createGlobalStyle } from "styled-components";

import { GRID_LEVELS, GridSize, gridLevel } from "./elements";
import { CompleteCheckbox } from "../../components/CompleteCheckbox";
import { IconButton } from "../../components/IconButton";
import {
  ArrowLeftIcon,
  CaretRightIcon,
  CheckIcon,
  SettingsIcon,
} from "../../components/Icons";
import { List, ListItem } from "../../components/List";
import { Menu, MenuHeader } from "../../components/Menu";
import {
  ResizeTransition,
  SlideLeftRightTransition,
} from "../../components/Transitions";
import { Caption1 } from "../../components/Typography";

/* ------------------------------------------------------------------ */
/* Board Settings menu — Grid granularity + Snap to Grid               */
/* ------------------------------------------------------------------ */

export const BOARD_MENU_WIDTH = 264;

/* The menu drills in place (index → Grid), so the paper clips the sliding
   panels and gives them a positioning context (mirrors the project "..." menu). */
export const BoardMenuSliderStyle = createGlobalStyle`
  .c-simple-menu__paper.board-settings-menu {
    overflow: hidden;
    position: relative;
  }
`;

export const StyledBoardSettingsMenu = styled.div`
  .toggle-item {
    justify-content: space-between;
  }

  .toggle-item .opt-left {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .toggle-item .grid-right {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }

  .toggle-item .grid-caret {
    flex-shrink: 0;
    fill: var(--color-theme-600);
  }

  .toggle-item .c-complete-checkbox {
    width: 16px;
    height: 16px;
    border-radius: 3px;
    align-self: center;
    flex-shrink: 0;
  }

  .snap-off {
    opacity: 0.5;
  }

  .option-item {
    justify-content: space-between;
  }

  .option-item .grid-check {
    flex-shrink: 0;
    fill: var(--color-primary);
  }
`;

export interface BoardSettingsMenuProps {
  gridSize: GridSize;
  snapToGrid: boolean;
  onGridSizeChange: (value: GridSize) => void;
  onSnapToGridChange: (value: boolean) => void;
}

export const BoardSettingsMenu = ({
  gridSize,
  snapToGrid,
  onGridSizeChange,
  onSnapToGridChange,
}: BoardSettingsMenuProps): ReactElement => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"index" | "grid">("index");
  const [isLeft, setIsLeft] = useState(true);
  const [height, setHeight] = useState(0);

  const measure = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setHeight(node.offsetHeight);
    }
  }, []);

  const goToGrid = (): void => {
    setView("grid");
    setIsLeft(true);
  };

  const backToIndex = (): void => {
    setView("index");
    setIsLeft(false);
  };

  const handleClose = (): void => {
    setOpen(false);
    setView("index");
    setIsLeft(true);
  };

  const snapDisabled = gridSize === "off";

  const indexView = (
    <StyledBoardSettingsMenu>
      <List style={{ padding: "8px 0" }} tabIndex={-1}>
        <ListItem className="toggle-item" onClick={goToGrid}>
          <span className="opt-left">Grid</span>
          <span className="grid-right">
            <Caption1 color="secondary">{gridLevel(gridSize).label}</Caption1>
            <CaretRightIcon className="grid-caret" width={16} height={16} />
          </span>
        </ListItem>
        <ListItem
          className={snapDisabled ? "toggle-item snap-off" : "toggle-item"}
          onClick={
            snapDisabled ? undefined : () => onSnapToGridChange(!snapToGrid)
          }
        >
          <span className="opt-left">Snap to Grid</span>
          <CompleteCheckbox
            primary
            completed={snapToGrid}
            disabled={snapDisabled}
          />
        </ListItem>
      </List>
    </StyledBoardSettingsMenu>
  );

  const gridView = (
    <StyledBoardSettingsMenu>
      <MenuHeader
        title="Grid"
        leftElement={
          <IconButton
            type="button"
            variant="text gray"
            size="small"
            onClick={backToIndex}
            aria-label="Back"
          >
            <ArrowLeftIcon />
          </IconButton>
        }
      />
      <List style={{ padding: "8px 0" }} tabIndex={-1}>
        {GRID_LEVELS.map((level) => (
          <ListItem
            key={level.value}
            className="option-item"
            onClick={() => {
              onGridSizeChange(level.value);
              backToIndex();
            }}
          >
            <span>{level.label}</span>
            {level.value === gridSize ? (
              <CheckIcon className="grid-check" width={16} height={16} />
            ) : null}
          </ListItem>
        ))}
      </List>
    </StyledBoardSettingsMenu>
  );

  return (
    <>
      <BoardMenuSliderStyle />
      <Menu
        open={open}
        onOpen={() => setOpen(true)}
        onClose={handleClose}
        position="bottom-end"
        menuClassName="board-settings-menu"
        target={
          <IconButton type="button" variant="text gray" active={open}>
            <SettingsIcon />
          </IconButton>
        }
      >
        <ResizeTransition in style={{ height, width: BOARD_MENU_WIDTH }}>
          <div style={{ position: "relative", overflow: "hidden" }}>
            <SlideLeftRightTransition
              in={view === "index"}
              direction={isLeft ? "left" : "right"}
            >
              <div ref={measure} style={{ width: BOARD_MENU_WIDTH }}>
                {indexView}
              </div>
            </SlideLeftRightTransition>
            <SlideLeftRightTransition
              in={view === "grid"}
              direction={isLeft ? "left" : "right"}
            >
              <div ref={measure} style={{ width: BOARD_MENU_WIDTH }}>
                {gridView}
              </div>
            </SlideLeftRightTransition>
          </div>
        </ResizeTransition>
      </Menu>
    </>
  );
};
